import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/components/language-provider";
import { Check, X, Zap, Star, Crown, Rocket } from "lucide-react";
import { APP_ROUTES, WEBSITE_ROUTES } from "@/lib/config";
import { usePlans, parseFeatures, PlanData } from "@/lib/use-plans";
import { planTextEs } from "@/lib/plans";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AnimatedSection } from "@/components/landing/animations";
import { PageHeader } from "@/components/page-header";

export default function Pricing() {
  const { language } = useLanguage();
  const { plans: apiPlans, loading: plansLoading } = usePlans();
  // Mensal/Anual: shown only when the admin filled an annual price for some plan.
  const [cycle, setCycle] = useState<"monthly" | "annual">("monthly");

  const t = {
    title: language === "pt" ? "Planos e Preços" : language === "es" ? "Planes y precios" : "Plans & Pricing",
    subtitle: language === "pt"
      ? "Escolha o plano ideal para sua necessidade. Comece gratuitamente e escale conforme cresce."
      : language === "es" ? "Elija el plan que se ajusta a sus necesidades. Empiece gratis y crezca a su ritmo." : "Choose the plan that fits your needs. Start free and scale as you grow.",
    monthly: language === "pt" ? "/mês" : language === "es" ? "/mes" : "/month",
    yearly: language === "pt" ? "/ano" : language === "es" ? "/año" : "/year",
    cycleMonthly: language === "pt" ? "Mensal" : language === "es" ? "Mensual" : "Monthly",
    cycleAnnual: language === "pt" ? "Anual" : language === "es" ? "Anual" : "Annual",
    save: (n: number) => language === "pt" ? `economize até ${n}%` : language === "es" ? `ahorre hasta ${n}%` : `save up to ${n}%`,
    perMonth: (v: string) => language === "pt" ? `≈ ${v}/mês` : language === "es" ? `≈ ${v}/mes` : `≈ ${v}/month`,
    startFree: language === "pt" ? "Começar Grátis" : language === "es" ? "Empezar gratis" : "Start Free",
    contactSales: language === "pt" ? "Falar com Vendas" : language === "es" ? "Hablar con ventas" : "Contact Sales",
    mostPopular: language === "pt" ? "Mais Popular" : language === "es" ? "Más popular" : "Most Popular",
    faqTitle: language === "pt" ? "Perguntas Frequentes" : language === "es" ? "Preguntas frecuentes" : "Frequently Asked Questions",
  };

  const handlePlanSelect = (planId: string, annual = false) => {
    const registerUrl = `${APP_ROUTES.register}?plan=${planId}${annual ? "&cycle=annual" : ""}`;
    window.location.href = registerUrl;
  };
  const money = (n: number) => language === "pt" ? `R$ ${n.toLocaleString("pt-BR", { maximumFractionDigits: 2 })}` : `$${n.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;

  const iconMap: Record<string, any> = { zap: Zap, star: Star, crown: Crown, rocket: Rocket };

  const hasAnnual = !!apiPlans?.some((p: PlanData) => !p.contactSales && Number(language === "pt" ? p.priceAnnualBrl : p.priceAnnualUsd) > 0);
  const savingsOf = (p: PlanData) => {
    const m = Number(language === "pt" ? p.priceMonthlyBrl : p.priceMonthlyUsd) || 0;
    const a = Number(language === "pt" ? p.priceAnnualBrl : p.priceAnnualUsd) || 0;
    return m > 0 && a > 0 ? Math.max(0, Math.round((1 - a / (m * 12)) * 100)) : 0;
  };
  const maxSavings = apiPlans ? Math.max(0, ...apiPlans.map(savingsOf)) : 0;

  const plans = apiPlans ? apiPlans.map((p: PlanData) => {
    const features = parseFeatures(p.features);
    const monthly = Number(language === "pt" ? p.priceMonthlyBrl : p.priceMonthlyUsd) || 0;
    const annual = Number(language === "pt" ? p.priceAnnualBrl : p.priceAnnualUsd) || 0;
    const annualChosen = cycle === "annual" && annual > 0 && !p.contactSales;
    return {
      id: p.planType.toLowerCase(),
      icon: iconMap[p.icon] || Star,
      name: p.name,
      price: p.contactSales ? (language === "pt" ? "Sob consulta" : language === "es" ? "A consultar" : "Contact us") : annualChosen ? money(annual) : money(monthly),
      period: p.contactSales ? "" : annualChosen ? t.yearly : t.monthly,
      perMonth: annualChosen ? t.perMonth(money(Math.round(annual / 12 * 100) / 100)) : "",
      savings: annualChosen ? savingsOf(p) : 0,
      description: language === "pt" ? p.descriptionPt : language === "es" ? planTextEs(p.descriptionEn, p.descriptionEs) : p.descriptionEn,
      features: features.map(f => ({ text: language === "pt" ? f.pt : language === "es" ? planTextEs(f.en, f.es) : f.en, included: f.included })),
      buttonText: p.contactSales ? t.contactSales : (language === "pt" ? `Escolher ${p.name}` : language === "es" ? `Elegir ${p.name}` : `Choose ${p.name}`),
      popular: p.popular,
      contactSales: p.contactSales,
      action: p.contactSales ? undefined : () => handlePlanSelect(p.planType.toLowerCase(), annualChosen),
    };
  }) : [
    {
      id: "free", icon: Zap, name: "Free",
      price: language === "pt" ? "R$ 0" : "$0", period: t.monthly,
      description: language === "pt" ? "Para começar a experimentar" : language === "es" ? "Para empezar" : "To get started",
      features: [
        { text: language === "pt" ? "3 assinaturas/mês" : language === "es" ? "3 firmas/mes" : "3 signatures/month", included: true },
        { text: language === "pt" ? "1 usuário" : language === "es" ? "1 usuario" : "1 user", included: true },
        { text: language === "pt" ? "Templates básicos" : language === "es" ? "Plantillas básicas" : "Basic templates", included: true },
        { text: "API", included: false },
      ],
      buttonText: t.startFree, popular: false, contactSales: false,
      action: () => handlePlanSelect("free"),
    },
    {
      id: "pro", icon: Star, name: "Professional",
      price: language === "pt" ? "R$ 199" : "$39", period: t.monthly,
      description: language === "pt" ? "Para equipes em crescimento" : language === "es" ? "Para equipos en crecimiento" : "For growing teams",
      features: [
        { text: language === "pt" ? "120 assinaturas/mês" : language === "es" ? "120 firmas/mes" : "120 signatures/month", included: true },
        { text: language === "pt" ? "5 usuários" : language === "es" ? "5 usuarios" : "5 users", included: true },
        { text: language === "pt" ? "Templates customizados" : language === "es" ? "Plantillas personalizadas" : "Custom templates", included: true },
        { text: "API + Webhooks", included: true },
      ],
      buttonText: language === "pt" ? "Escolher Professional" : language === "es" ? "Elegir Professional" : "Choose Professional",
      popular: true, contactSales: false,
      action: () => handlePlanSelect("pro"),
    },
    {
      id: "enterprise", icon: Crown, name: "Enterprise",
      price: language === "pt" ? "Sob consulta" : language === "es" ? "A consultar" : "Contact us", period: "",
      description: language === "pt" ? "Para grandes organizações" : language === "es" ? "Para grandes organizaciones" : "For large organizations",
      features: [
        { text: language === "pt" ? "Assinaturas ilimitadas" : language === "es" ? "Firmas ilimitadas" : "Unlimited signatures", included: true },
        { text: language === "pt" ? "Usuários ilimitados" : language === "es" ? "Usuarios ilimitados" : "Unlimited users", included: true },
        { text: "Dynamics 365 + SSO", included: true },
        { text: language === "pt" ? "Suporte 24/7" : language === "es" ? "Soporte 24/7" : "24/7 Support", included: true },
      ],
      buttonText: t.contactSales, popular: false, contactSales: true,
    },
  ];

  const pricingFaqs = [
    { q: language === "pt" ? "Posso mudar de plano depois?" : language === "es" ? "¿Puedo cambiar de plan después?" : "Can I change plans later?", a: language === "pt" ? "Sim, você pode fazer upgrade ou downgrade a qualquer momento." : language === "es" ? "Sí, puede subir o bajar de plan en cualquier momento." : "Yes, you can upgrade or downgrade at any time." },
    { q: language === "pt" ? "Existe período de teste?" : language === "es" ? "¿Hay período de prueba?" : "Is there a trial period?", a: language === "pt" ? "O plano gratuito funciona como período de teste. Experimente sem compromisso." : language === "es" ? "El plan gratis funciona como período de prueba. Pruébelo sin compromiso." : "The free plan works as a trial period. Try it with no commitment." },
    { q: language === "pt" ? "Quais formas de pagamento?" : language === "es" ? "¿Qué formas de pago aceptan?" : "What payment methods?", a: language === "pt" ? "Cartão de crédito." : language === "es" ? "Tarjeta de crédito." : "Credit card." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PageHeader />

      {/* Hero */}
      <section className="py-16 sm:py-20 md:py-28 blob-bg">
        <div className="container mx-auto px-4 text-center">
          <AnimatedSection>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">{t.title}</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto text-lg">{t.subtitle}</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Plans: pulled 32px up into the hero, so it must sit above it (the hero is positioned for its background
          blobs and, painted later, was covering the top of the Mensal/Anual buttons). */}
      <section className="pb-20 -mt-8 relative z-10">
        <div className="container mx-auto px-4">
          {hasAnnual && (
            <div className="mb-8 flex justify-center" data-testid="pricing-cycle">
              <div className="inline-flex rounded-full border bg-background p-1 shadow-sm">
                <button type="button" onClick={() => setCycle("monthly")} className={`rounded-full px-5 py-2 text-sm font-medium transition ${cycle === "monthly" ? "text-white" : "text-muted-foreground"}`} style={cycle === "monthly" ? { background: "var(--fs-forest)" } : {}} aria-pressed={cycle === "monthly"} data-testid="pricing-cycle-monthly">{t.cycleMonthly}</button>
                <button type="button" onClick={() => setCycle("annual")} className={`rounded-full px-5 py-2 text-sm font-medium transition ${cycle === "annual" ? "text-white" : "text-muted-foreground"}`} style={cycle === "annual" ? { background: "var(--fs-forest)" } : {}} aria-pressed={cycle === "annual"} data-testid="pricing-cycle-annual">
                  {t.cycleAnnual}{maxSavings > 0 && <span className="ml-2 rounded-full px-2 py-0.5 text-xs" style={{ background: "var(--fs-amber)", color: "#fff" }}>{t.save(maxSavings)}</span>}
                </button>
              </div>
            </div>
          )}
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, i) => {
              const Icon = plan.icon;
              return (
                <AnimatedSection key={plan.id} delay={i * 0.12}>
                  <div className={`card-organic p-6 h-full flex flex-col relative ${plan.popular ? "ring-2" : ""}`} style={plan.popular ? { borderColor: "var(--fs-amber)", boxShadow: "0 8px 30px hsla(42, 75%, 45%, 0.15)" } : {}}>
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="font-medium" style={{ background: "var(--fs-amber)", color: "#fff" }}>{t.mostPopular}</Badge>
                      </div>
                    )}
                    <div className="mb-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: plan.popular ? "var(--fs-amber)" : "var(--fs-forest)", color: "#fff" }}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <h3 className="text-xl font-semibold">{plan.name}</h3>
                      <p className="text-sm text-muted-foreground mt-1">{plan.description}</p>
                    </div>
                    <div className="mb-6">
                      <span className="text-3xl font-bold" data-testid={`pricing-price-${plan.id}`}>{plan.price}</span>
                      {plan.period && <span className="text-muted-foreground text-sm">{plan.period}</span>}
                      {"perMonth" in plan && plan.perMonth && (
                        <p className="text-xs text-muted-foreground mt-1">{plan.perMonth}{plan.savings ? <span className="ml-1" style={{ color: "var(--fs-sage)" }}>· {plan.savings}% off</span> : null}</p>
                      )}
                    </div>
                    <ul className="space-y-3 mb-8 flex-1">
                      {plan.features.map((f, j) => (
                        <li key={j} className="flex items-center gap-2.5 text-sm">
                          {f.included ? <Check className="w-4 h-4 flex-shrink-0" style={{ color: "var(--fs-sage)" }} /> : <X className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />}
                          <span className={f.included ? "" : "text-muted-foreground/50"}>{f.text}</span>
                        </li>
                      ))}
                    </ul>
                    {plan.contactSales ? (
                      <Link href={WEBSITE_ROUTES.enterpriseContact}>
                        <Button variant="outline" className="w-full rounded-full">{plan.buttonText}</Button>
                      </Link>
                    ) : (
                      <button onClick={plan.action} className={`w-full rounded-full py-2.5 font-medium transition-all ${plan.popular ? "btn-cta" : "border hover:bg-muted"}`}>
                        {plan.buttonText}
                      </button>
                    )}
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 md:py-28" style={{ background: "linear-gradient(180deg, hsl(var(--muted) / 0.3) 0%, transparent 100%)" }}>
        <div className="container mx-auto px-4 max-w-3xl">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold">{t.faqTitle}</h2>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <Accordion type="single" collapsible className="space-y-3">
              {pricingFaqs.map((faq, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border rounded-xl px-6 bg-card">
                  <AccordionTrigger className="hover:no-underline py-4"><span className="text-left font-medium">{faq.q}</span></AccordionTrigger>
                  <AccordionContent className="pb-4 text-muted-foreground leading-relaxed">{faq.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>
        </div>
      </section>

      <footer className="py-8 border-t text-center text-sm text-muted-foreground">
        <div className="container mx-auto px-4">© {new Date().getFullYear()} FillSign.</div>
      </footer>
    </div>
  );
}
